/**
 * useBodyScrollLock - Freezes the page behind a modal, sheet or popup.
 *
 * Call it from any overlay with the overlay's open flag:
 *
 *	useBodyScrollLock(isOpen);
 *
 * Why a shared hook instead of `document.body.style.overflow = 'hidden'`
 * sprinkled through each overlay:
 *
 *	- Stacking. Overlays open on top of each other (a ConfirmModal over a
 *	  Modal, a lightbox over the workflow). With per-component locks the
 *	  first one to close reset overflow to '' and the page behind the
 *	  still-open overlay started scrolling again. A module-level counter
 *	  means the page only unlocks when the LAST overlay closes.
 *	- iOS Safari ignores `overflow: hidden` on <body> for touch scrolling,
 *	  so a swipe on the backdrop still dragged the page underneath. Pinning
 *	  the body with `position: fixed` (offset by the current scroll) is the
 *	  reliable fix; we restore the scroll position on unlock so the user
 *	  lands exactly where they were.
 *	- Desktop layout shift. Hiding the scrollbar widens the page by the
 *	  scrollbar's width and everything jumps sideways; padding the body by
 *	  that width keeps the content still.
 *
 * Scrolling INSIDE the overlay (a modal body with overflow-y-auto) still
 * works — only the document behind it is frozen.
 */
import { useEffect } from 'react';

/** How many overlays currently hold the lock. */
let lockCount = 0;

/** Inline styles and scroll position captured when the first lock is taken. */
let saved: {
	scrollY: number;
	htmlOverflow: string;
	bodyOverflow: string;
	bodyPosition: string;
	bodyTop: string;
	bodyWidth: string;
	bodyPaddingRight: string;
} | null = null;

const lock = () => {
	lockCount += 1;
	if (lockCount > 1) return;

	const { documentElement: html, body } = document;
	const scrollY = window.scrollY;
	// Measure before hiding it — afterwards the difference is always 0.
	const scrollbarWidth = window.innerWidth - html.clientWidth;

	saved = {
		scrollY,
		htmlOverflow: html.style.overflow,
		bodyOverflow: body.style.overflow,
		bodyPosition: body.style.position,
		bodyTop: body.style.top,
		bodyWidth: body.style.width,
		bodyPaddingRight: body.style.paddingRight,
	};

	// Some browsers honor overflow on <html>, some on <body> — set both.
	html.style.overflow = 'hidden';
	body.style.overflow = 'hidden';
	// position:fixed is what actually stops iOS touch scrolling; top keeps
	// the page visually where it was instead of jumping to the top.
	body.style.position = 'fixed';
	body.style.top = `-${scrollY}px`;
	body.style.width = '100%';
	if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
};

const unlock = () => {
	lockCount = Math.max(0, lockCount - 1);
	if (lockCount > 0 || !saved) return;

	const { documentElement: html, body } = document;
	html.style.overflow = saved.htmlOverflow;
	body.style.overflow = saved.bodyOverflow;
	body.style.position = saved.bodyPosition;
	body.style.top = saved.bodyTop;
	body.style.width = saved.bodyWidth;
	body.style.paddingRight = saved.bodyPaddingRight;
	// Un-fixing the body resets scroll to 0 — put the user back.
	window.scrollTo(0, saved.scrollY);
	saved = null;
};

export const useBodyScrollLock = (active: boolean) => {
	useEffect(() => {
		if (!active) return;
		lock();
		return unlock;
	}, [active]);
};

export default useBodyScrollLock;
